// Vendor Libs
import {chain, cloneDeep, defaultsDeep, get, mapValues, set, unset} from 'lodash';

// Config
import getBootstrapInitialState from '../bootstrap-data/forms-initial-states';
import {applyPreferenceState} from '../../config/preferences-config';

let preferenceStates = {};

function getFormInitialState(formName) {
    const state = getBootstrapInitialState(formName);
    const preferenceState = preferenceStates[formName];

    return applyPreferenceState('formData', preferenceState, state, formName);
}

const actions = {
    FORM_REGISTER: (state, {formName}) => {
        const newState = cloneDeep(state);

        return defaultsDeep(
            {
                [formName]: getFormInitialState(formName),
            },
            newState
        );
    },

    FORM_UNREGISTER: (state, {formName}) =>
        chain(state)
            .cloneDeep()
            .omit(formName)
            .value(),

    FORM_SET_FIELD_VALUE: (state, {formName, fieldName, operator, value}) => {
        const newValue = value !== '' ? value : undefined;

        return chain(state)
            .cloneDeep()
            .set(`${formName}.values.${fieldName}`, newValue)
            .set(`${formName}.operators.${fieldName}`, operator)
            .value();
    },

    FORM_SET_VALUES: (state, {formName, values, resetValues}) => {
        const newState = cloneDeep(state);
        const formValues = resetValues ? values : {...get(newState, `${formName}.values`), ...values};

        return set(newState, `${formName}.values`, formValues);
    },

    SET_FORM_REQUEST_PATH: (state, {formName, requestPath}) =>
        chain(state)
            .cloneDeep()
            .set(`${formName}.requestPath`, requestPath)
            .value(),

    FORM_RESET: (state, {formName, submitted, resetValues, resetStore}) => {
        let newState;

        if (formName) {
            newState = set(
                cloneDeep(state),
                formName,
                resetValues ? getBootstrapInitialState(formName) : getFormInitialState(formName)
            );

            set(newState, [formName, 'submitted'], submitted);
        } else if (!resetStore) {
            newState = mapValues(state, formName => getBootstrapInitialState(formName));
        } else {
            newState = {};
        }

        return newState;
    },

    FORM_UNREGISTER_FIELD_VALUE: (state, {formName, fieldName}) => {
        const newState = cloneDeep(state);

        unset(newState, `${formName}.values.${fieldName}`);
        return newState;
    },

    FORM_SET_FIELD_ERRORS: (state, {formName, fieldName, errors}) => {
        const newState = cloneDeep(state);

        return defaultsDeep(
            {
                [formName]: {
                    fieldErrors: {
                        [fieldName]: errors,
                    },
                },
            },
            newState
        );
    },

    FORM_SUBMITTED: (state, {formName, submitted}) =>
        chain(state)
            .cloneDeep()
            .set(`${formName}.submitted`, submitted)
            .value(),

    FORM_UNREGISTER_FIELD_ERRORS: (state, {formName, fieldName}) => {
        const newState = cloneDeep(state);

        unset(newState, [formName, 'fieldErrors', fieldName]);
        return newState;
    },

    FORM_UNREGISTER_FORM_ERRORS: (state, {formName}) =>
        chain(state)
            .cloneDeep()
            .set(`${formName}.formErrors`, getFormInitialState(formName).formErrors)
            .value(),

    FORM_SUBMIT: (state, {formName}) => {
        const newState = cloneDeep(state);

        return defaultsDeep(
            {
                [formName]: {
                    fetched: false,
                    fetching: true,
                    formErrors: null,
                    location: null,
                    response: null,
                    submitted: false,
                },
            },
            newState
        );
    },

    FORM_SUBMIT_FULFILLED: (state, {formName, response}) =>
        chain(state)
            .cloneDeep()
            .merge({
                [formName]: {
                    fetched: true,
                    fetching: false,
                    response,
                },
            })
            .value(),

    FORM_SUBMIT_REJECTED: (state, {formName, formErrors}) =>
        chain(state)
            .cloneDeep()
            .merge({
                [formName]: {
                    fetched: true,
                    fetching: false,
                    formErrors,
                },
            })
            .value(),

    FORM_SUBMIT_RESET: (state, {formName, resetValues}) => {
        let newState = cloneDeep(state);

        if (resetValues) {
            newState = chain(state)
                .cloneDeep()
                .set(formName, set(getFormInitialState(formName), 'submitted', false))
                .value();
        } else {
            newState = chain(state)
                .cloneDeep()
                .merge({
                    [formName]: {
                        fetched: false,
                        fetching: false,
                        formErrors: null,
                        location: null,
                        response: null,
                    },
                })
                .value();
        }

        return newState;
    },

    FORM_SUBMIT_ASYNC_LOCATION: (state, {formName, location}) => {
        const newState = cloneDeep(state);

        return set(newState, [formName, 'location'], location);
    },

    PREFERENCES_LOAD: (state, {stores}) => {
        preferenceStates = get(stores, 'formData', {});

        return state;
    },

    PREFERENCES_SAVE: (state, {stores}) => {
        preferenceStates = get(stores, 'formData', {});

        return state;
    },

    PREFERENCES_RESET: state => {
        preferenceStates = {};

        return state;
    },
};

const reducer = (state = {}, action) => (actions[action.type] ? actions[action.type](state, action) : state);

export default reducer;
